import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FormProvider, useForm, FieldErrors, useWatch } from 'react-hook-form';
import { useQuery, useMutation } from 'react-query';
import Layout from 'components/common/layout/Layout';
import LoadingLayout from 'components/common/layout/LoadingLayout';
import FormAttachedFile from 'components/common/forms/FormAttachedFile';
import SingleBtn from 'components/common/elements/SingleBtn';
import styles from 'styles/pages/common/NoticeRegister.module.scss';
import {
  NoticeRegisterDefaultValues,
  NoticeEditDefaultValues,
} from 'constants/notice';
import {
  RegisterNoticeFormFields,
  TRegisterNoticeForm,
  TNoticeFileList,
} from 'types/form/notice';
import { getToday } from 'utils/date';
import useModal from 'hooks/useModal';
import useGoBack from 'hooks/useGoBack';
import { getNotice, postNotice, putNotice } from 'apis/notice';
import { TCustomAxiosError, isTCustomAxiosError } from 'apis/config/axios';

const NoticeRegistration = () => {
  const { openModal } = useModal();
  const goToList = useGoBack();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const id = Number(query.get('id'));
  const navigate = useNavigate();
  const [defaultFormValues, setDefaultFormValues] = useState<
    TRegisterNoticeForm | undefined
  >(id ? NoticeEditDefaultValues : NoticeRegisterDefaultValues);
  const [date, setDate] = useState<string>(getToday());
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(!!id);

  const methods = useForm<TRegisterNoticeForm>({
    defaultValues: defaultFormValues,
  });
  const { register, getValues, setValue, reset, handleSubmit, control } =
    methods;

  const title = useWatch({
    name: RegisterNoticeFormFields.Title,
    control,
  });

  const content = useWatch({
    name: RegisterNoticeFormFields.Content,
    control,
  });

  useQuery('getNoticeInfo', () => getNotice(id), {
    onSuccess: ({ data: noticeInfo }) => {
      setIsLoading(false);
      const formValuesFromData: TRegisterNoticeForm = {
        title: noticeInfo.title,
        content: noticeInfo.content,
      };
      reset(formValuesFromData);
      setDefaultFormValues(formValuesFromData);
      setDate(noticeInfo.createdAt);
      setAttachmentFiles(
        noticeInfo.noticeFileList.map((file: TNoticeFileList) => {
          return new File([], file.originFileName, {
            type: `fileid:${file.noticeFileId}`,
          });
        })
      );
    },
    onError: () =>
      openModal({
        title: '공지사항 조회 실패',
        text: '다시 시도해 주세요.',
        onSubmit: () => goToList(),
        onClickOutside: () => goToList(),
      }),
    enabled: !!id,
  });

  const handleCancel = () =>
    openModal({
      isConfirm: true,
      title: `공지사항 ${id ? '수정' : '등록'} 취소`,
      text: '해당 페이지에서 나가면 작성 중인 내용이 저장되지 않을 수 있습니다.',
      onSubmit: () => {
        if (id && location.state?.from) {
          navigate(-1);
        } else {
          goToList();
        }
      },
    });

  const invalid = (errors: FieldErrors<TRegisterNoticeForm>) =>
    openModal({
      title: `공지사항 ${id ? '수정' : '등록'} 불가`,
      text: `${Object.values(errors)
        .map((error) => error.message)
        .join(', ')}을 입력해 주세요.`,
    });

  const onSubmit = (data: TRegisterNoticeForm) =>
    openModal({
      isConfirm: true,
      title: `공지사항 ${id ? '수정' : '등록'}`,
      text: `공지사항을 ${id ? '수정' : '등록'}하시겠습니까?`,
      onCancel: () => setIsLoading(false),
      onSubmit: () => {
        setIsLoading(true);
        const formData = new FormData();

        formData.append(
          'request',
          new Blob([JSON.stringify(data)], {
            type: 'application/json',
          })
        );
        for (let i = 0; i < attachmentFiles.length; i++) {
          if (!attachmentFiles[i].type.includes('fileid')) {
            formData.append('files', attachmentFiles[i]);
          }
        }
        if (id) {
          putNoticeMutation(formData);
        } else {
          postNoticeMutation(formData);
        }
      },
    });

  const { mutate: postNoticeMutation } = useMutation(postNotice, {
    onSuccess: () => {
      setIsLoading(false);
      goToList();
    },
    onError: (error) => onError(error as TCustomAxiosError),
  });

  const { mutate: putNoticeMutation } = useMutation(
    (formData: FormData) => putNotice(id, formData),
    {
      onSuccess: () => {
        setIsLoading(false);
        goToList();
      },
      onError: (error) => onError(error as TCustomAxiosError),
    }
  );

  const onError = (error: TCustomAxiosError) => {
    setIsLoading(false);
    if (isTCustomAxiosError(error)) {
      openModal({
        title: `공지사항 ${id ? '수정' : '등록'} 실패`,
        text:
          error.code === 400 || error.code === 413
            ? error.message
            : error.code === 417
            ? '입력한 정보를 다시 확인해 주세요.'
            : '카이아이 고객센터로 문의해 주세요.',
      });
    }
  };

  return (
    <>
      {isLoading ? (
        <LoadingLayout />
      ) : (
        <Layout>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit, invalid)}>
              <h1 className="main_title mt40 mb24">
                공지사항 {id ? '수정' : '등록'}
              </h1>
              <div className={styles.title_wrapper}>
                <input
                  key={RegisterNoticeFormFields.Title}
                  type="text"
                  {...register(RegisterNoticeFormFields.Title, {
                    required: '제목',
                  })}
                  placeholder="공지사항 제목을 입력해 주세요."
                  className={styles.input}
                />
                <span className="fs14 px24">{date}</span>
              </div>
              <textarea
                key={RegisterNoticeFormFields.Content}
                id="notice"
                {...register(RegisterNoticeFormFields.Content, {
                  required: '내용',
                })}
                rows={10}
                className={styles.textarea}
                placeholder="공지사항을 입력해 주세요."
              />
              <FormAttachedFile
                isBoard={true}
                name="uploadFile"
                files={attachmentFiles}
                inputId="uploadFile"
                multiple={true}
                maxLength={5}
                className="mt24"
                listStyle={{ marginLeft: '80px' }}
                onDelete={(index) =>
                  setValue(RegisterNoticeFormFields.DeleteFileIdList, [
                    ...(getValues(RegisterNoticeFormFields.DeleteFileIdList) ||
                      []),
                    index,
                  ])
                }
                onChange={(files) => setAttachmentFiles(files)}
              />
              <div className="flex gap16 justify_end mt32 mb40">
                <SingleBtn
                  type="button"
                  btnText="취소"
                  btnStyle={{
                    width: '97px',
                    fontSize: '14px',
                  }}
                  onClick={handleCancel}
                />
                <SingleBtn
                  type="submit"
                  btnText="등록"
                  isBtnActive={true}
                  btnStyle={{
                    width: '97px',
                    fontSize: '14px',
                  }}
                  disabled={!title || !content}
                />
              </div>
            </form>
          </FormProvider>
        </Layout>
      )}
    </>
  );
};

export default NoticeRegistration;
